import React, { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedImage from "./AuthenticatedImage";

const extractGoogleDriveFileId = (url = "") => {
  const match = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  return match ? match[1] : null;
};

/**
 * Check if URL is a server proxy image that requires authentication
 */
const isServerProxyImage = (url = "") => {
  if (!url) return false;
  // Match /api/images/* and /api/documents/* endpoints (both absolute and
  // relative URLs). Both are authenticated proxies that require a Bearer token,
  // so they must be fetched via AuthenticatedImage rather than a plain <img>.
  return /\/api\/(images|documents)\//.test(url);
};

const buildImageCandidates = (url = "") => {
  // If it's a server proxy URL, don't build candidates - use as-is
  if (isServerProxyImage(url)) {
    return [url];
  }

  const fileId = extractGoogleDriveFileId(url);

  if (!fileId) {
    return [url].filter(Boolean);
  }

  return [
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w4000`,
    `https://drive.google.com/uc?export=view&id=${fileId}`,
    `https://drive.google.com/uc?id=${fileId}`,
    url,
  ];
};

const buildDrivePreviewUrl = (url = "") => {
  const fileId = extractGoogleDriveFileId(url);
  return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
};

const PRESET_SIZES = {
  medium: { width: "760px", height: "560px" },
  large: { width: "960px", height: "720px" },
};

const ImagePreviewModal = ({ imageUrl, onClose }) => {
  const imageCandidates = useMemo(() => buildImageCandidates(imageUrl), [imageUrl]);
  const [imageIndex, setImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("medium");
  const [hasLoadError, setHasLoadError] = useState(false);
  const [showDrivePreview, setShowDrivePreview] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef(null);
  
  const isAuthenticatedImage = isServerProxyImage(imageUrl);

  useEffect(() => {
    setImageIndex(0);
    setIsLoading(true);
    setHasLoadError(false);
    setShowDrivePreview(false);
    setPosition({ x: 0, y: 0 });
  }, [imageUrl]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragStateRef.current) return;

      const { startX, startY, originX, originY } = dragStateRef.current;
      setPosition({
        x: originX + (event.clientX - startX),
        y: originY + (event.clientY - startY),
      });
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const currentImageSrc = imageCandidates[imageIndex];
  const hasAnotherCandidate = imageIndex < imageCandidates.length - 1;
  const modalSize = PRESET_SIZES[selectedSize];
  const drivePreviewUrl = buildDrivePreviewUrl(imageUrl);

  const handleDragStart = (event) => {
    if (event.target.closest("button, a")) return;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-transparent p-4"
      onClick={onClose}
    >
      <div
        className="relative flex min-w-[320px] max-w-[90vw] min-h-[260px] max-h-[90vh] flex-col overflow-hidden rounded-[10px] bg-white shadow-2xl ring-1 ring-black/10"
        style={{
          width: modalSize.width,
          height: modalSize.height,
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3 cursor-grab"
          onMouseDown={handleDragStart}
        >
          <div className="min-w-0 flex-1 pr-4">
            <p className="text-sm font-semibold leading-none text-gray-900">Image Preview</p>
            {!isLoading && !showDrivePreview && hasLoadError && (
              <p className="truncate pt-1 text-xs text-gray-500">{imageUrl}</p>
            )}
          </div>
          <div className="ml-4 flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 rounded-xl bg-gray-100 p-1">
              {Object.keys(PRESET_SIZES).map((sizeKey) => (
                <button
                  key={sizeKey}
                  type="button"
                  onClick={() => setSelectedSize(sizeKey)}
                  className={`rounded-lg px-2 py-1 text-xs font-medium capitalize transition ${
                    selectedSize === sizeKey
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {sizeKey}
                </button>
              ))}
            </div>
            {/* <a
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
              aria-label="Open image in new tab"
              title="Open image in new tab"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
              </svg>
            </a> */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
              aria-label="Close preview"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-auto bg-white p-6">
          {isLoading && !showDrivePreview && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
                <span className="text-sm text-gray-700">Loading image...</span>
              </div>
            </div>
          )}

          {!showDrivePreview && currentImageSrc ? (
            isAuthenticatedImage ? (
              <AuthenticatedImage
                src={currentImageSrc}
                alt="Preview"
                className="h-full w-full rounded-[2px] bg-white object-contain"
                style={{ display: hasLoadError ? 'none' : 'block' }}
                onClick={null}
                onLoad={() => {
                  setIsLoading(false);
                  setHasLoadError(false);
                }}
                onError={(err) => {
                  console.error('Failed to load authenticated image:', err);
                  setIsLoading(false);
                  setHasLoadError(true);
                }}
              />
            ) : (
              <img
                src={currentImageSrc}
                alt="Preview"
                className={`${hasLoadError ? "hidden" : "block"} h-full w-full rounded-[2px] bg-white object-contain`}
                onLoad={() => {
                  setIsLoading(false);
                  setHasLoadError(false);
                }}
                onError={() => {
                  if (hasAnotherCandidate) {
                    setImageIndex((current) => current + 1);
                    return;
                  }
                  if (drivePreviewUrl) {
                    setShowDrivePreview(true);
                    setIsLoading(false);
                    setHasLoadError(false);
                    return;
                  }
                  setIsLoading(false);
                  setHasLoadError(true);
                }}
              />
            )
          ) : null}

          {showDrivePreview && drivePreviewUrl && (
            <iframe
              src={drivePreviewUrl}
              title="Google Drive Preview"
              className="h-full w-full rounded-[10px] bg-white"
            />
          )}

          {!isLoading && !showDrivePreview && (!currentImageSrc || hasLoadError) && (
            <div className="flex h-full items-center justify-center rounded-[2px] border border-dashed border-gray-300 bg-white">
              <div className="px-6 text-center">
                <p className="text-sm font-medium text-gray-700">
                  {currentImageSrc ? "The image could not be loaded." : "No image URL was provided."}
                </p>
                {currentImageSrc && <p className="mt-1 text-xs text-gray-500">This often happens when the Google Drive file is not directly viewable.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
